import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause, Trash2, RotateCcw, Save, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Daily supervisor voice-note recorder for the Line Tracking Output Entry section.
 *
 * Behaviour:
 *   - Uses the browser's MediaRecorder API to capture audio (webm/opus by default).
 *   - Users can preview locally before hitting "Save note".
 *   - Notes are saved against (date, line_id) — order_number is optional.
 *   - Every note recorded for the currently-selected date+line is listed below with
 *     a play button. Admins (or roles granted `delete_supervisor_notes`) see a delete icon.
 *
 * Access control: parent decides whether to render this component based on the
 * user's `record_supervisor_note` / `view_supervisor_notes` capabilities.
 */
export default function SupervisorVoiceNotes({
  date,
  lineId,
  orderNumber,
  canRecord,
  canView,
  canDelete,
}) {
  const [notes, setNotes] = useState([]);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [audioBlobUrls, setAudioBlobUrls] = useState({}); // id → objectURL

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const audioRef = useRef(null);

  const loadNotes = async () => {
    if (!canView || !date || !lineId) return;
    try {
      const { data } = await api.get("/supervisor-notes", { params: { date_from: date, date_to: date, line_id: lineId } });
      setNotes(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadNotes(); /* eslint-disable-next-line */ }, [date, lineId, canView]);

  // Clean up any live streams / blob URLs when unmounting
  useEffect(() => () => {
    stopStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    Object.values(audioBlobUrls).forEach((u) => URL.revokeObjectURL(u));
    if (tickRef.current) clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!canRecord) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser can't access the microphone. Try Chrome or Safari on mobile.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Prefer webm/opus (Chrome/Firefox); Safari falls back to mp4/aac automatically.
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setPreviewBlob(blob);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        stopStream();
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startedAtRef.current), 200);
    } catch (e) {
      toast.error("Mic permission denied — enable microphone access in your browser settings");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const discardPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null); setPreviewUrl(null); setElapsedMs(0);
  };

  const saveNote = async () => {
    if (!previewBlob) return;
    if (!date || !lineId) { toast.error("Pick a date and line first"); return; }
    setSaving(true);
    try {
      const b64 = await blobToBase64(previewBlob);
      const { data } = await api.post("/supervisor-notes", {
        date,
        line_id: lineId,
        order_number: orderNumber || null,
        audio_base64: b64,
        mime_type: previewBlob.type || "audio/webm",
        duration_ms: elapsedMs,
      });
      toast.success("Voice note saved");
      setNotes((prev) => [data, ...prev]);
      discardPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save note");
    }
    setSaving(false);
  };

  const playNote = async (n) => {
    // Toggle pause
    if (playingId === n.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      let url = audioBlobUrls[n.id];
      if (!url) {
        const { data } = await api.get(`/supervisor-notes/${n.id}/audio`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        setAudioBlobUrls((prev) => ({ ...prev, [n.id]: url }));
      }
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlayingId(null);
      await audioRef.current.play();
      setPlayingId(n.id);
    } catch {
      toast.error("Couldn't play audio");
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Delete this voice note? This cannot be undone.")) return;
    try {
      await api.delete(`/supervisor-notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast.success("Note deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!canRecord && !canView) return null;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 sm:p-4" data-testid="supervisor-voice-notes">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><Volume2 className="h-4 w-4 text-brand" /> Daily voice note</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">Record a short note about today's activity for this line. Audible to admin & production team.</div>
        </div>
        {canRecord && !recording && !previewUrl && (
          <Button
            type="button"
            onClick={startRecording}
            className="bg-brand hover:bg-brand-dark text-white h-10"
            data-testid="voice-record-btn"
          >
            <Mic className="h-4 w-4 mr-2" /> Start recording
          </Button>
        )}
      </div>

      {/* Recording state */}
      {recording && (
        <div className="mt-3 flex items-center justify-between rounded-md bg-red-50 border border-red-200 px-3 py-2" data-testid="voice-recording-state">
          <div className="flex items-center gap-2 text-red-800 font-mono text-[13px]">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
            </span>
            Recording · {formatDuration(elapsedMs)}
          </div>
          <Button type="button" onClick={stopRecording} className="h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="voice-stop-btn">
            <Square className="h-4 w-4 mr-2" /> Stop
          </Button>
        </div>
      )}

      {/* Preview state */}
      {previewUrl && !recording && (
        <div className="mt-3 rounded-md bg-white border border-slate-200 p-3" data-testid="voice-preview">
          <div className="text-[12px] font-semibold text-slate-700 mb-2">Preview ({formatDuration(elapsedMs)})</div>
          <audio controls src={previewUrl} className="w-full h-10" data-testid="voice-preview-audio" />
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button type="button" onClick={saveNote} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="voice-save-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save note</>}
            </Button>
            <Button type="button" variant="outline" onClick={() => { discardPreview(); startRecording(); }} className="h-10" data-testid="voice-retake-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Re-record
            </Button>
            <Button type="button" variant="outline" onClick={discardPreview} className="h-10 text-red-700 border-red-200 hover:bg-red-50" data-testid="voice-discard-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Discard
            </Button>
          </div>
        </div>
      )}

      {/* Saved notes for today */}
      {canView && (
        <div className="mt-3">
          <div className="text-[11.5px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Today's notes {notes.length > 0 && <span className="text-slate-400">({notes.length})</span>}
          </div>
          {notes.length === 0 ? (
            <div className="text-[12px] text-slate-400 italic">No voice notes recorded for this line today.</div>
          ) : (
            <ul className="space-y-1.5">
              {notes.map((n) => {
                const isPlaying = playingId === n.id;
                return (
                  <li key={n.id} className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-2.5 py-1.5" data-testid={`voice-note-${n.id}`}>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0 shrink-0"
                      onClick={() => playNote(n)}
                      data-testid={`voice-play-${n.id}`}
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-slate-800 truncate">
                        {n.recorded_by} <span className="text-slate-400 font-normal">· {n.recorded_by_role}</span>
                      </div>
                      <div className="text-[10.5px] font-mono text-slate-500">
                        {new Date(n.created_at).toLocaleString()} {n.duration_ms ? `· ${formatDuration(n.duration_ms)}` : ""}
                      </div>
                    </div>
                    {canDelete && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 shrink-0 text-slate-400 hover:text-red-600"
                        onClick={() => deleteNote(n)}
                        data-testid={`voice-delete-${n.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function formatDuration(ms) {
  const s = Math.floor((ms || 0) / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, "0")}`;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const idx = String(dataUrl).indexOf(",");
      resolve(idx > -1 ? String(dataUrl).slice(idx + 1) : String(dataUrl));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
